export type EmailComposerRecovery = {
  savedAt: number;
  pending?: boolean;
  draftMessageId: number | null;
  draftUpdatedAt: string | null;
  draftIdempotencyKey: string;
  sendIdempotencyKey: string;
  accountId: number | null;
  from: string;
  subject: string;
  body: string;
  toInput: string;
  ccInput: string;
  bccInput: string;
  toRecipients: string[];
  ccRecipients: string[];
  bccRecipients: string[];
  attachments: Array<{
    id: string;
    filename: string;
    contentBase64?: string;
    contentType: string;
    size: number;
    persistedId?: number;
  }>;
};

const DATABASE = 'experviser-email-composer-recovery';
const STORE = 'snapshots';
const pendingWrites = new Map<string, Promise<void>>();

function database(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (!window.indexedDB) return reject(new Error('IndexedDB unavailable'));
    const request = window.indexedDB.open(DATABASE, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

export async function readEmailComposerRecovery(key: string): Promise<EmailComposerRecovery | null> {
  const db = await database();
  try {
    return await new Promise((resolve, reject) => {
      const request = db.transaction(STORE, 'readonly').objectStore(STORE).get(key);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result ?? null);
    });
  } finally { db.close(); }
}

export async function isEmailComposerRecoveryRetired(key: string, draftKey: string): Promise<boolean> {
  const prefix = key.match(/^experviser:composer-recovery:v2:\d+:/)?.[0];
  if (!prefix || !draftKey) return false;
  const db = await database();
  try {
    return await new Promise((resolve, reject) => {
      const request = db.transaction(STORE, 'readonly').objectStore(STORE).get(`${prefix}retired:${draftKey}`);
      request.onsuccess = () => resolve(request.result?.retired === true);
      request.onerror = () => reject(request.error);
    });
  } finally { db.close(); }
}

async function writeSnapshot(key: string, snapshot: EmailComposerRecovery): Promise<void> {
  const db = await database();
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(STORE, 'readwrite');
      transaction.objectStore(STORE).put(snapshot, key);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  } finally { db.close(); }
}

export function writeEmailComposerRecovery(key: string, snapshot: EmailComposerRecovery): Promise<void> {
  const previous = pendingWrites.get(key) ?? Promise.resolve();
  const next = previous.catch(() => {}).then(() => writeSnapshot(key, snapshot));
  pendingWrites.set(key, next);
  void next.finally(() => { if (pendingWrites.get(key) === next) pendingWrites.delete(key); }).catch(() => {});
  return next;
}

/** Retire all owner-scoped copies of this draft after explicit delete/send.
 * The marker prevents an older copy from another tab being offered again. */
export async function clearEmailComposerRecovery(key: string, draftKey?: string | null): Promise<void> {
  const ownerPrefix = key.match(/^experviser:composer-recovery:v2:\d+:/)?.[0];
  await Promise.all([...pendingWrites.entries()].filter(([pendingKey]) => pendingKey === key
    || (draftKey && ownerPrefix && pendingKey.startsWith(ownerPrefix))).map(([, work]) => work.catch(() => {})));
  const db = await database();
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(STORE, 'readwrite');
      const snapshots = transaction.objectStore(STORE);
      snapshots.delete(key);
      if (draftKey && ownerPrefix) {
        snapshots.put({ retired: true, draftIdempotencyKey: draftKey }, `${ownerPrefix}retired:${draftKey}`);
        const request = snapshots.openCursor(IDBKeyRange.bound(ownerPrefix, `${ownerPrefix}\uffff`));
        request.onsuccess = () => {
          const cursor = request.result;
          if (!cursor) return;
          if (!cursor.value?.retired && cursor.value?.draftIdempotencyKey === draftKey) cursor.delete();
          cursor.continue();
        };
      }
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  } finally { db.close(); }
}

export type EmailComposerRecoveryChoice = { key: string; snapshot: EmailComposerRecovery };

/** A closed tab must not make an unsaved owner-scoped snapshot unreachable.
 * Prefer the newest copy per stable draft key before filtering pending copies,
 * so an older tab snapshot cannot resurrect a draft already saved elsewhere. */
export async function listEmailComposerRecoveryChoices(
  ownerId: number, context: string, currentKey: string,
): Promise<EmailComposerRecoveryChoice[]> {
  if (!Number.isSafeInteger(ownerId) || ownerId <= 0) return [];
  const prefix = `experviser:composer-recovery:v2:${ownerId}:`;
  const db = await database();
  try {
    const families = new Map<string, EmailComposerRecoveryChoice>();
    const retired = new Set<string>();
    await new Promise<void>((resolve, reject) => {
      const request = db.transaction(STORE, 'readonly').objectStore(STORE)
        .openCursor(IDBKeyRange.bound(prefix, `${prefix}\uffff`));
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) return resolve();
        const key = String(cursor.key);
        const snapshot = cursor.value as EmailComposerRecovery;
        if (cursor.value?.retired && typeof snapshot.draftIdempotencyKey === 'string') retired.add(snapshot.draftIdempotencyKey);
        if (key.endsWith(`:${context}`) && Number.isFinite(snapshot?.savedAt)
          && typeof snapshot.draftIdempotencyKey === 'string') {
          const previous = families.get(snapshot.draftIdempotencyKey);
          if (!previous || previous.snapshot.savedAt < snapshot.savedAt) families.set(snapshot.draftIdempotencyKey, { key, snapshot });
        }
        cursor.continue();
      };
    });
    return [...families.values()].filter(({ key, snapshot }) => key !== currentKey
      && snapshot.pending !== false && !retired.has(snapshot.draftIdempotencyKey)
      && (snapshot.subject?.trim() || snapshot.body?.trim() || snapshot.attachments?.length))
      .sort((a, b) => b.snapshot.savedAt - a.snapshot.savedAt);
  } finally { db.close(); }
}
